/**
 * WatchedButton Component
 * Click to mark as watched + rate with stars
 * Shows on media cards and detail pages
 * Uses portal to render popup at body level (avoids overflow clipping)
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import StarRating from './StarRating';
import { getRating, setRating, removeRating } from '../../services/ratings';

interface WatchedButtonProps {
  mediaType: 'movie' | 'tv';
  id: number;
  title: string;
  posterPath?: string;
  variant?: 'card' | 'detail' | 'mini';
}

export default function WatchedButton({
  mediaType,
  id,
  title,
  posterPath,
  variant = 'card',
}: WatchedButtonProps) {
  const [currentRating, setCurrentRating] = useState(0);
  const [showRatingPopup, setShowRatingPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentRating(getRating(mediaType, id));
  }, [mediaType, id]);

  // Update popup position when shown
  useEffect(() => {
    if (showRatingPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [showRatingPopup]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setShowRatingPopup(false);
      }
    };

    if (showRatingPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRatingPopup]);

  const handleRatingChange = (rating: number) => {
    setCurrentRating(rating);
    setRating(mediaType, id, title, rating, posterPath);
  };

  const handleRemove = () => {
    setCurrentRating(0);
    removeRating(mediaType, id);
    setShowRatingPopup(false);
  };

  const isWatched = currentRating > 0;

  // Rating popup rendered via portal (at body level, never clipped)
  const ratingPopup = showRatingPopup && createPortal(
    <div
      ref={popupRef}
      className="fixed p-3 bg-gray-900 rounded-lg shadow-2xl border border-white/20 z-[9999] min-w-[160px]"
      style={{
        top: popupPosition.top,
        left: popupPosition.left,
        transform: 'translateX(-50%)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs text-gray-400 mb-2 whitespace-nowrap text-center">Rate this</p>
      <StarRating rating={currentRating} onChange={handleRatingChange} size="md" />
      {isWatched && (
        <button
          onClick={handleRemove}
          className="mt-2 text-xs text-red-400 hover:text-red-300 w-full text-center"
        >
          Remove rating
        </button>
      )}
    </div>,
    document.body
  );

  // Mini variant - just an icon badge
  if (variant === 'mini') {
    return (
      <>
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowRatingPopup(!showRatingPopup);
          }}
          className={`p-1.5 rounded-full transition-all ${
            isWatched
              ? 'bg-green-500 text-white'
              : 'bg-black/60 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/80'
          }`}
          title={isWatched ? `Rated ${currentRating}★` : 'Mark as watched'}
        >
          {isWatched ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
        {ratingPopup}
      </>
    );
  }

  // Card variant - shows on hover
  if (variant === 'card') {
    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowRatingPopup(!showRatingPopup);
          }}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
            isWatched
              ? 'bg-green-500/90 text-white'
              : 'bg-black/70 backdrop-blur-sm text-white/80 hover:bg-black/90 hover:text-white'
          }`}
        >
          {isWatched ? (
            <>
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{currentRating}★</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>Watched?</span>
            </>
          )}
        </button>

        {/* Rating Popup */}
        {showRatingPopup && (
          <div
            ref={popupRef}
            className="absolute bottom-full left-0 mb-2 p-3 bg-gray-900 rounded-lg shadow-xl border border-white/10 z-50 min-w-[180px]"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-medium mb-2 line-clamp-1">{title}</p>
            <p className="text-xs text-gray-400 mb-2">How would you rate it?</p>
            <StarRating rating={currentRating} onChange={handleRatingChange} size="lg" />
            {isWatched && (
              <button
                onClick={handleRemove}
                className="mt-3 text-xs text-red-400 hover:text-red-300 w-full text-left"
              >
                Remove rating
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Detail variant - larger, inline
  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-400">Your Rating:</span>
        <StarRating
          rating={currentRating}
          onChange={handleRatingChange}
          size="lg"
          showValue
        />
      </div>
      {isWatched && (
        <button
          onClick={handleRemove}
          className="text-sm text-red-400 hover:text-red-300"
        >
          Clear
        </button>
      )}
    </div>
  );
}
